import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout, Menu, Button, Typography, Space, Drawer, Grid, Spin } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  TeamOutlined,
  FundProjectionScreenOutlined,
  ContactsOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  BellOutlined,
  DatabaseOutlined,
  InboxOutlined,
  AuditOutlined,
  UserAddOutlined,
  ShopOutlined,
  LeftOutlined,
  RightOutlined,
  RollbackOutlined,
  SkinOutlined,
  DollarOutlined,
  AccountBookOutlined,
  BankOutlined,
  ApartmentOutlined,
  BarChartOutlined,
  WalletOutlined,
  LinkOutlined,
  AppstoreOutlined,
  FundOutlined,
  BarcodeOutlined,
  SafetyCertificateOutlined,
  NodeIndexOutlined,
  FileExcelOutlined,
  HomeOutlined,
  PercentageOutlined,
  CalculatorOutlined,
  ExclamationCircleOutlined,
  UnorderedListOutlined,
  ScheduleOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  AreaChartOutlined,
  AlertOutlined,
  ScanOutlined,
  PieChartOutlined,
  FileSearchOutlined,
  ReconciliationOutlined,
  CameraOutlined,
  HistoryOutlined,
  ApiOutlined,
  SendOutlined,
} from '@ant-design/icons';
import QuickCaptureModal from './components/QuickCaptureModal';
import ResourceGuard from './components/ResourceGuard';
import ChatDrawer from './components/ChatDrawer';
import { AuthProvider, ProtectedRoute, useAuth } from './context/AuthContext';
import { PipelineStagesProvider } from './context/PipelineStagesContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationBell from './components/NotificationBell';
import GlobalSearch from './components/GlobalSearch'; // global search 2026-09-06
import Login from './pages/Login';
import Dashboard from './pages/purchasing/Dashboard';
import MRList from './pages/purchasing/MRList';
import MRCreate from './pages/purchasing/MRCreate';
import MRView from './pages/purchasing/MRView';
import POList from './pages/purchasing/POList';
import POCreate from './pages/purchasing/POCreate';
import POView from './pages/purchasing/POView';
import Admin from './pages/admin/Admin';
import CatalogList from './pages/purchasing/CatalogList';
import GRNList from './pages/purchasing/GRNList';
import GRNView from './pages/purchasing/GRNView';
import AuditTrail from './pages/purchasing/AuditTrail';
const VendorList = React.lazy(() => import('./pages/purchasing/VendorList'));
const VendorDetail = React.lazy(() => import('./pages/purchasing/VendorDetail'));
const VendorPendingQueue = React.lazy(() => import('./pages/purchasing/VendorPendingQueue'));
const ItemCard = React.lazy(() => import('./pages/purchasing/ItemCard'));
const ComposerSync = React.lazy(() => import('./pages/purchasing/ComposerSync'));
const ReturnList = React.lazy(() => import('./pages/purchasing/ReturnList'));
const Cancellations = React.lazy(() => import('./pages/purchasing/Cancellations')); // queue full page (2026-09-16)
const ClientSamples = React.lazy(() => import('./pages/purchasing/client-samples'));
const CostAllocation = React.lazy(() => import('./pages/purchasing/CostAllocation')); // Cost allocation 2026-09-05
// CRM pages — lazy-loaded for code splitting (Phase 5.4)
const CRMDashboard = React.lazy(() => import('./pages/crm/CRMDashboard'));
const CRMWorkspace = React.lazy(() => import('./pages/crm/CRMWorkspace'));
const CRMClientList = React.lazy(() => import('./pages/crm/CRMClientList'));
const CRMClientView = React.lazy(() => import('./pages/crm/CRMClientView'));
const CRMClientEdit = React.lazy(() => import('./pages/crm/CRMClientCreate'));
const CRMDealList = React.lazy(() => import('./pages/crm/CRMDealList'));
const CRMDealCreate = React.lazy(() => import('./pages/crm/CRMDealCreate'));
const CRMDealView = React.lazy(() => import('./pages/crm/CRMDealView'));
const CRMPipeline = React.lazy(() => import('./pages/crm/CRMPipeline'));
const CRMTaskList = React.lazy(() => import('./pages/crm/CRMTaskList'));
const CRMFollowUps = React.lazy(() => import('./pages/crm/CRMFollowUps'));
const CRMAppointments = React.lazy(() => import('./pages/crm/CRMAppointments'));
const CRMGateController = React.lazy(() => import('./pages/crm/CRMGateController'));
const CRMLeadCapture = React.lazy(() => import('./pages/crm/CRMLeadCapture'));
const CRMFormFieldAdmin = React.lazy(() => import('./pages/crm/CRMFormFieldAdmin'));
// CRM Follow-Up Admin (Phase 5A)
const CRMFollowUpRules = React.lazy(() => import('./pages/crm/CRMFollowUpRules'));
const CRMFollowUpSettings = React.lazy(() => import('./pages/crm/CRMFollowUpSettings'));
const CRMFollowUpObservability = React.lazy(() => import('./pages/crm/CRMFollowUpObservability'));
const CRMClientMessages = React.lazy(() => import('./pages/crm/CRMClientMessages'));
// Calendar module — company-wide scheduling
const CalendarHome = React.lazy(() => import('./pages/calendar/CalendarHome'));
const CalendarRoomList = React.lazy(() => import('./pages/calendar/CalendarRoomList'));
// Accounting pages — lazy-loaded
const AccChartOfAccounts = React.lazy(() => import('./pages/accounting/ChartOfAccounts'));
const AccCostCenters = React.lazy(() => import('./pages/accounting/CostCenters'));
const AccJournalEntries = React.lazy(() => import('./pages/accounting/JournalEntries'));
const AccJournalEntryView = React.lazy(() => import('./pages/accounting/JournalEntryView'));
const AccJournalEntryCreate = React.lazy(() => import('./pages/accounting/JournalEntryCreate'));
const AccPayments = React.lazy(() => import('./pages/accounting/Payments'));
const AccPaymentView = React.lazy(() => import('./pages/accounting/PaymentView'));
const AccReconciliationView = React.lazy(() => import('./pages/accounting/ReconciliationView'));
const AccTrialBalance = React.lazy(() => import('./pages/accounting/TrialBalance'));
const AccGeneralLedger = React.lazy(() => import('./pages/accounting/GeneralLedger'));
const AccInvoiceList = React.lazy(() => import('./pages/accounting/InvoiceList'));
const AccInvoiceCreate = React.lazy(() => import('./pages/accounting/InvoiceCreate'));
const AccInvoiceView = React.lazy(() => import('./pages/accounting/InvoiceView'));
const AccVendorBillList = React.lazy(() => import('./pages/accounting/VendorBillList'));
const AccVendorBillCreate = React.lazy(() => import('./pages/accounting/VendorBillCreate'));
const AccVendorBillView = React.lazy(() => import('./pages/accounting/VendorBillView'));
const AccBalanceSheet = React.lazy(() => import('./pages/accounting/BalanceSheetReport'));
const AccProfitAndLoss = React.lazy(() => import('./pages/accounting/ProfitAndLossReport'));
const AccCashFlow = React.lazy(() => import('./pages/accounting/CashFlowReport'));
const AccAgedReceivables = React.lazy(() => import('./pages/accounting/AgedReceivables'));
const AccAgedPayables = React.lazy(() => import('./pages/accounting/AgedPayables'));
const AccPartnerLedger = React.lazy(() => import('./pages/accounting/PartnerLedger'));
const AccDashboard = React.lazy(() => import('./pages/accounting/AccountingDashboard'));
const AccJournalKanban = React.lazy(() => import('./pages/accounting/JournalKanban'));
const AccBankReconciliation = React.lazy(() => import('./pages/accounting/BankReconciliation'));
const AccBankReconciliationWorkspace = React.lazy(() => import('./pages/accounting/BankReconciliationWorkspace'));
// Accounting Phase 10 — Tax Reports
const AccVATReturn = React.lazy(() => import('./pages/accounting/VATReturn'));
const AccWithholdingTaxReport = React.lazy(() => import('./pages/accounting/WithholdingTaxReport'));
const AccCurrencyRates = React.lazy(() => import('./pages/accounting/CurrencyRates'));
const AccFxRevaluation = React.lazy(() => import('./pages/accounting/FxRevaluation'));
// Accounting Phase 12 — Fiscal Year & Lock Dates
const AccFiscalYearSettings = React.lazy(() => import('./pages/accounting/FiscalYearSettings'));
// Accounting Phase 13 — AR Collection Management
const AccCollectionManagement = React.lazy(() => import('./pages/accounting/CollectionManagement'));
const AccCollectionSettings = React.lazy(() => import('./pages/accounting/CollectionSettings'));
// Accounting Phase 15 — Fixed Assets & Depreciation
const AccAssetList = React.lazy(() => import('./pages/accounting/AssetList'));
const AccAssetCreate = React.lazy(() => import('./pages/accounting/AssetCreate'));
const AccAssetView = React.lazy(() => import('./pages/accounting/AssetView'));
// Accounting Phase 16 — Deferred Revenue & Expenses
const AccDeferredEntries = React.lazy(() => import('./pages/accounting/DeferredEntries'));
// Accounting Phase 18 — Audit Trail & Compliance
const AccAuditCompliance = React.lazy(() => import('./pages/accounting/AuditCompliance'));
// Accounting Phase 19 — Bridge Health (Cross-Module Integration)
const AccBridgeHealth = React.lazy(() => import('./pages/accounting/BridgeHealth'));
// Accounting Phase 20 — Cost Center Deep Analytics
const AccCostCenterAnalytics = React.lazy(() => import('./pages/accounting/CostCenterAnalytics'));
const AccDealProfitability = React.lazy(() => import('./pages/accounting/DealProfitability'));
// Accounting Phase 17 — Settings & Configuration
const AccSettingsPage = React.lazy(() => import('./pages/accounting/settings/AccountingSettings'));
// Design System Prototype — Option 1 style guide (Lane A + Lane B showcase)
const DesignSystemPrototype = React.lazy(() => import('./pages/DesignSystemPrototype'));
// Inventory pages — Phase 1 (Code Dictionary admin + Part Number Builder host)
const InventoryCodeDictionary = React.lazy(() => import('./pages/inventory/CodeDictionary'));
// Inventory pages — Phase 2 (Warehouses, Locations, Stock On Hand)
const InventoryWarehouseList = React.lazy(() => import('./pages/inventory/WarehouseList'));
const InventoryWarehouseForm = React.lazy(() => import('./pages/inventory/WarehouseForm'));
const InventoryLocationTree = React.lazy(() => import('./pages/inventory/LocationTree'));
const InventoryStockOnHand = React.lazy(() => import('./pages/inventory/StockOnHand'));
// Inventory pages — Phase 3 (Stock Moves, Pickings, Lots + operation wrappers)
const InventoryStockMoveHistory = React.lazy(() => import('./pages/inventory/StockMoveHistory'));
const InventoryStockMoveView = React.lazy(() => import('./pages/inventory/StockMoveView'));
const InventoryReceiptCreate = React.lazy(() => import('./pages/inventory/ReceiptCreate'));
const InventoryIssueCreate = React.lazy(() => import('./pages/inventory/IssueCreate'));
const InventoryTransferCreate = React.lazy(() => import('./pages/inventory/TransferCreate'));
const InventoryScrapCreate = React.lazy(() => import('./pages/inventory/ScrapCreate'));
const InventoryPickingList = React.lazy(() => import('./pages/inventory/PickingList'));
const InventoryPickingCreate = React.lazy(() => import('./pages/inventory/PickingCreate'));
const InventoryPickingView = React.lazy(() => import('./pages/inventory/PickingView'));
const InventoryLotList = React.lazy(() => import('./pages/inventory/LotList'));
const InventoryLotView = React.lazy(() => import('./pages/inventory/LotView'));
// Inventory pages — Phase 4 (Serial numbers, Quality, Putaway)
const InventorySerialNumberList = React.lazy(() => import('./pages/inventory/SerialNumberList'));
const InventorySerialNumberForm = React.lazy(() => import('./pages/inventory/SerialNumberForm'));
const InventorySerialNumberView = React.lazy(() => import('./pages/inventory/SerialNumberView'));
const InventoryQualityPlans = React.lazy(() => import('./pages/inventory/QualityInspectionPlans'));
const InventoryQualityQueue = React.lazy(() => import('./pages/inventory/QualityInspectionQueue'));
const InventoryQualityForm = React.lazy(() => import('./pages/inventory/QualityInspectionForm'));
const InventoryPutawayRules = React.lazy(() => import('./pages/inventory/PutawayRules'));
// Inventory pages — Phase 5 (Audit opening-balance import)
const InventoryAuditImport = React.lazy(() => import('./pages/inventory/AuditImport'));
// Inventory pages — Phase 7 (Landed Cost + FX Revaluation + Valuation Layers)
const InventoryLandedCostList = React.lazy(() => import('./pages/inventory/LandedCostList'));
const InventoryLandedCostCreate = React.lazy(() => import('./pages/inventory/LandedCostCreate'));
const InventoryLandedCostView = React.lazy(() => import('./pages/inventory/LandedCostView'));
const InventoryRevaluationReport = React.lazy(() => import('./pages/inventory/RevaluationReport'));
const InventoryValuationLayers = React.lazy(() => import('./pages/inventory/ValuationLayers'));
// Inventory pages — Phase 8 (Physical Counts, ABC, Cycle Counts, Approval Rules)
const PhysicalCountList = React.lazy(() => import('./pages/inventory/PhysicalCountList'));
const PhysicalCountCreate = React.lazy(() => import('./pages/inventory/PhysicalCountCreate'));
const PhysicalCountView = React.lazy(() => import('./pages/inventory/PhysicalCountView'));
const CycleCountSchedule = React.lazy(() => import('./pages/inventory/CycleCountSchedule'));
const CycleCountPolicies = React.lazy(() => import('./pages/inventory/CycleCountPolicies'));
const AbcClassificationReport = React.lazy(() => import('./pages/inventory/AbcClassificationReport'));
const ApprovalRules = React.lazy(() => import('./pages/inventory/ApprovalRules'));
// Inventory pages — Phase 9 (Kits, Consignment, Dropship, In-Transit)
const KitList = React.lazy(() => import('./pages/inventory/KitList'));
const KitBuilder = React.lazy(() => import('./pages/inventory/KitBuilder'));
const KitAssemble = React.lazy(() => import('./pages/inventory/KitAssemble'));
const ConsignmentReport = React.lazy(() => import('./pages/inventory/ConsignmentReport'));
const DropshipPickings = React.lazy(() => import('./pages/inventory/DropshipPickings'));
const InTransitTransfers = React.lazy(() => import('./pages/inventory/InTransitTransfers'));
// Inventory pages — Phase 10 (RMA)
const RmaList = React.lazy(() => import('./pages/inventory/RmaList'));
const RmaCreate = React.lazy(() => import('./pages/inventory/RmaCreate'));
const RmaView = React.lazy(() => import('./pages/inventory/RmaView'));
// Inventory Phase 11 — Demand Forecasting & MRP
const ForecastViewer = React.lazy(() => import('./pages/inventory/ForecastViewer'));
const MrpRunHistory = React.lazy(() => import('./pages/inventory/MrpRunHistory'));
const MrpRunProposals = React.lazy(() => import('./pages/inventory/MrpRunProposals'));
const ReorderAlerts = React.lazy(() => import('./pages/inventory/ReorderAlerts'));
const ProjectedOnHandChart = React.lazy(() => import('./pages/inventory/ProjectedOnHandChart'));
const ReorderRulesAdmin = React.lazy(() => import('./pages/inventory/ReorderRulesAdmin'));
// Inventory Phase 12 — WIP Accounting
const WipValuationReport = React.lazy(() => import('./pages/inventory/WipValuationReport'));
const WipAgingReport = React.lazy(() => import('./pages/inventory/WipAgingReport'));
// Inventory Phase 13 — Barcode & Scanning
const BarcodeLabels = React.lazy(() => import('./pages/inventory/BarcodeLabels'));
const MobileScanner = React.lazy(() => import('./pages/inventory/MobileScanner'));
// Inventory Phase 14 — Reports, Snapshots, Reconciliation
const StockAgingReport = React.lazy(() => import('./pages/inventory/StockAgingReport'));
const DeadStockReport = React.lazy(() => import('./pages/inventory/DeadStockReport'));
const SlowMovingReport = React.lazy(() => import('./pages/inventory/SlowMovingReport'));
const StockTurnoverReport = React.lazy(() => import('./pages/inventory/StockTurnoverReport'));
const ReconciliationDashboard = React.lazy(() => import('./pages/inventory/ReconciliationDashboard'));
const SnapshotAdmin = React.lazy(() => import('./pages/inventory/SnapshotAdmin'));
const PointInTimeQuery = React.lazy(() => import('./pages/inventory/PointInTimeQuery'));
// Inventory Phase 15 — Dashboard, Webhooks
const InventoryDashboard = React.lazy(() => import('./pages/inventory/InventoryDashboard'));
const WebhookSubscriptions = React.lazy(() => import('./pages/inventory/WebhookSubscriptions'));
const WebhookDeliveries = React.lazy(() => import('./pages/inventory/WebhookDeliveries'));

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const SIDER_WIDTH = 200;
const SIDER_COLLAPSED_WIDTH = 80;

const AppLayout: React.FC = () => {
  const { user, logout, isAdmin, canAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/mrs')) return 'mrs';
    if (path.startsWith('/pos')) return 'pos';
    if (path.startsWith('/grns')) return 'grns';
    if (path.startsWith('/returns')) return 'returns';
    if (path.startsWith('/catalog')) return 'catalog';
    if (path.startsWith('/client-samples')) return 'client-samples';
    if (path.startsWith('/cost-allocation')) return 'cost-allocation';
    if (path.startsWith('/audit')) return 'audit';
    if (path.startsWith('/admin')) return 'admin';
    if (path === '/crm' || path === '/crm/dashboard') return 'crm-dashboard';
    if (path === '/crm/workspace') return 'crm-workspace';
    if (path.startsWith('/crm/clients')) return 'crm-clients';
    if (path === '/crm/deals/pipeline') return 'crm-pipeline';
    if (path.startsWith('/crm/deals')) return 'crm-deals';
    if (path.startsWith('/crm/tasks')) return 'crm-tasks';
    if (path.startsWith('/crm/follow-ups')) return 'crm-follow-ups';
    if (path.startsWith('/crm/admin/follow-up-rules')) return 'crm-follow-up-rules';
    if (path.startsWith('/crm/admin/follow-up-settings')) return 'crm-follow-up-settings';
    if (path.startsWith('/crm/admin/follow-up-observability')) return 'crm-follow-up-observability';
    if (path.startsWith('/crm/client-messages')) return 'crm-client-messages';
    if (path.startsWith('/accounting/invoices')) return 'acc-invoices';
    if (path.startsWith('/accounting/vendor-bills')) return 'acc-vendor-bills';
    if (path.startsWith('/accounting/accounts')) return 'acc-accounts';
    if (path.startsWith('/accounting/cost-centers')) return 'acc-cost-centers';
    if (path.startsWith('/accounting/entries')) return 'acc-entries';
    if (path.startsWith('/accounting/payments')) return 'acc-payments';
    if (path.startsWith('/accounting/reconciliation')) return 'acc-reconciliation';
    if (path.startsWith('/accounting/trial-balance')) return 'acc-trial-balance';
    if (path.startsWith('/accounting/ledger')) return 'acc-ledger';
    if (path.startsWith('/accounting/balance-sheet')) return 'acc-balance-sheet';
    if (path.startsWith('/accounting/profit-and-loss')) return 'acc-profit-and-loss';
    if (path.startsWith('/accounting/cash-flow')) return 'acc-cash-flow';
    if (path.startsWith('/accounting/aged-receivables')) return 'acc-aged-receivables';
    if (path.startsWith('/accounting/aged-payables')) return 'acc-aged-payables';
    if (path.startsWith('/accounting/partner-ledger')) return 'acc-partner-ledger';
    if (path.startsWith('/accounting/dashboard')) return 'acc-dashboard';
    if (path.startsWith('/accounting/journal-kanban')) return 'acc-journal-kanban';
    if (path.startsWith('/accounting/bank-reconciliation')) return 'acc-bank-reconciliation';
    if (path.startsWith('/accounting/vat-return')) return 'acc-vat-return';
    if (path.startsWith('/accounting/withholding-tax')) return 'acc-withholding-tax';
    if (path.startsWith('/accounting/currency-rates')) return 'acc-currency-rates';
    if (path.startsWith('/accounting/fx-revaluation')) return 'acc-fx-revaluation';
    if (path.startsWith('/accounting/fiscal-periods')) return 'acc-fiscal-periods';
    if (path.startsWith('/accounting/collections/settings')) return 'acc-collection-settings';
    if (path.startsWith('/accounting/collections')) return 'acc-collections';
    if (path.startsWith('/accounting/assets')) return 'acc-assets';
    if (path.startsWith('/accounting/deferred')) return 'acc-deferred';
    if (path.startsWith('/accounting/audit-compliance')) return 'acc-audit-compliance';
    if (path.startsWith('/accounting/bridge-health')) return 'acc-bridge-health';
    if (path.startsWith('/accounting/cost-center-analytics')) return 'acc-cc-analytics';
    if (path.startsWith('/accounting/deal-profitability')) return 'acc-deal-profitability';
    if (path.startsWith('/accounting/settings')) return 'acc-settings';
    if (path.startsWith('/inventory/code-dictionary')) return 'inv-code-dictionary';
    if (path.startsWith('/inventory/stock-on-hand')) return 'inv-stock-on-hand';
    if (path.startsWith('/inventory/warehouses')) return 'inv-warehouses';
    if (path.startsWith('/inventory/stock-moves')) return 'inv-stock-moves';
    if (path.startsWith('/inventory/pickings')) return 'inv-pickings';
    if (path.startsWith('/inventory/lots')) return 'inv-lots';
    if (path.startsWith('/inventory/serial-numbers')) return 'inv-serials';
    if (path.startsWith('/inventory/quality')) return 'inv-quality';
    if (path.startsWith('/inventory/putaway-rules')) return 'inv-putaway';
    if (path.startsWith('/inventory/audit-import')) return 'inv-audit-import';
    if (path.startsWith('/inventory/landed-costs')) return 'inv-landed-costs';
    if (path.startsWith('/inventory/revaluation')) return 'inv-revaluation';
    if (path.startsWith('/inventory/valuation-layers')) return 'inv-valuation-layers';
    if (path.startsWith('/inventory/physical-counts')) return 'inv-physical-counts';
    if (path.startsWith('/inventory/cycle-count-policies')) return 'inv-cycle-count-policies';
    if (path.startsWith('/inventory/cycle-count-schedule')) return 'inv-cycle-count-schedule';
    if (path.startsWith('/inventory/abc-report')) return 'inv-abc-report';
    if (path.startsWith('/inventory/approval-rules')) return 'inv-approval-rules';
    if (path.startsWith('/inventory/kits')) return 'inv-kits';
    if (path.startsWith('/inventory/kit-assemble')) return 'inv-kit-assemble';
    if (path.startsWith('/inventory/consignment')) return 'inv-consignment';
    if (path.startsWith('/inventory/dropship')) return 'inv-dropship';
    if (path.startsWith('/inventory/in-transit')) return 'inv-in-transit';
    if (path.startsWith('/inventory/rmas')) return 'inv-rmas';
    if (path.startsWith('/inventory/forecasts')) return 'inv-forecasts';
    if (path.startsWith('/inventory/mrp/runs')) return 'inv-mrp-runs';
    if (path.startsWith('/inventory/mrp/proposals')) return 'inv-mrp-proposals';
    if (path.startsWith('/inventory/projected-on-hand')) return 'inv-projected';
    if (path.startsWith('/inventory/reorder-alerts')) return 'inv-reorder-alerts';
    if (path.startsWith('/inventory/reorder-rules')) return 'inv-reorder-rules';
    if (path.startsWith('/inventory/wip/valuation')) return 'inv-wip-valuation';
    if (path.startsWith('/inventory/wip/aging')) return 'inv-wip-aging';
    if (path.startsWith('/inventory/barcode-labels')) return 'inv-barcode-labels';
    if (path.startsWith('/inventory/scanner')) return 'inv-scanner';
    if (path.startsWith('/inventory/reports/aging')) return 'inv-report-aging';
    if (path.startsWith('/inventory/reports/dead-stock')) return 'inv-report-dead-stock';
    if (path.startsWith('/inventory/reports/slow-moving')) return 'inv-report-slow-moving';
    if (path.startsWith('/inventory/reports/turnover')) return 'inv-report-turnover';
    if (path.startsWith('/inventory/reports/reconciliation')) return 'inv-report-reconciliation';
    if (path.startsWith('/inventory/reports/snapshots')) return 'inv-report-snapshots';
    if (path.startsWith('/inventory/reports/stock-at')) return 'inv-report-stock-at';
    if (path.startsWith('/inventory/dashboard')) return 'inv-dashboard';
    if (path.startsWith('/inventory/webhooks/deliveries')) return 'inv-webhook-deliveries';
    if (path.startsWith('/inventory/webhooks')) return 'inv-webhooks';
    return 'dashboard';
  };

  const showCRM = canAccess('crm');

  const menuItems: any[] = [];

  // ── Purchasing Module ──
  const purchasingChildren: any[] = [];
  if (canAccess('purchasing.dashboard')) {
    purchasingChildren.push({ key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard', onClick: () => navigate('/') });
  }
  if (canAccess('purchasing.mrs')) {
    purchasingChildren.push({ key: 'mrs', icon: <FileTextOutlined />, label: 'Material Requests', onClick: () => navigate('/mrs') });
  }
  if (canAccess('purchasing.pos')) {
    purchasingChildren.push({ key: 'pos', icon: <ShoppingCartOutlined />, label: 'Purchase Orders', onClick: () => navigate('/pos') });
  }
  if (canAccess('purchasing.grns')) {
    purchasingChildren.push({ key: 'grns', icon: <InboxOutlined />, label: 'Goods Receiving', onClick: () => navigate('/grns') });
  }
  if (canAccess('purchasing.returns')) {
    purchasingChildren.push({ key: 'returns', icon: <RollbackOutlined />, label: 'Purchase Returns', onClick: () => navigate('/returns') });
  }
  if (canAccess('purchasing.vendors')) {
    purchasingChildren.push({ key: 'vendors', icon: <ShopOutlined />, label: 'Vendors', onClick: () => navigate('/vendors') });
  }
  if (canAccess('purchasing.catalog')) {
    purchasingChildren.push({ key: 'catalog', icon: <DatabaseOutlined />, label: 'Parts Catalog', onClick: () => navigate('/catalog') });
  }
  if (canAccess('purchasing.client_samples')) {
    purchasingChildren.push({ key: 'client-samples', icon: <SkinOutlined />, label: 'Client Samples', onClick: () => navigate('/client-samples') });
  }
  if (canAccess('purchasing.cost_centers')) {
    purchasingChildren.push({ key: 'cost-allocation', icon: <PieChartOutlined />, label: 'Cost Allocation', onClick: () => navigate('/cost-allocation') });
  }
  if (canAccess('purchasing.audit')) {
    purchasingChildren.push({ key: 'audit', icon: <AuditOutlined />, label: 'Audit Trail', onClick: () => navigate('/audit') });
  }
  if (purchasingChildren.length > 0) {
    menuItems.push({ key: 'purchasing-group', label: 'PURCHASING', type: 'group', children: purchasingChildren });
  }

  // ── CRM Module ──
  if (showCRM) {
    const crmChildren: any[] = [];
    if (canAccess('crm.dashboard')) {
      crmChildren.push({ key: 'crm-dashboard', icon: <FundProjectionScreenOutlined />, label: 'CRM Dashboard', onClick: () => navigate('/crm') });
    }
    if (canAccess('crm.workspace')) {
      crmChildren.push({ key: 'crm-workspace', icon: <UserOutlined />, label: 'My Workspace', onClick: () => navigate('/crm/workspace') });
    }
    if (canAccess('crm.clients')) {
      crmChildren.push({ key: 'crm-clients', icon: <ContactsOutlined />, label: 'Clients', onClick: () => navigate('/crm/clients') });
    }
    if (canAccess('crm.deals')) {
      crmChildren.push({ key: 'crm-deals', icon: <ProjectOutlined />, label: 'Deals', onClick: () => navigate('/crm/deals') });
    }
    if (canAccess('crm.pipeline')) {
      crmChildren.push({ key: 'crm-pipeline', icon: <FundProjectionScreenOutlined />, label: 'Pipeline', onClick: () => navigate('/crm/deals/pipeline') });
    }
    if (canAccess('crm.tasks')) {
      crmChildren.push({ key: 'crm-tasks', icon: <CheckSquareOutlined />, label: 'Tasks', onClick: () => navigate('/crm/tasks') });
    }
    if (canAccess('crm.follow_ups')) {
      crmChildren.push({ key: 'crm-follow-ups', icon: <BellOutlined />, label: 'Follow-ups', onClick: () => navigate('/crm/follow-ups') });
    }
    if (canAccess('crm.appointments')) {
      crmChildren.push({ key: 'crm-appointments', icon: <CalendarOutlined />, label: 'Appointments', onClick: () => navigate('/crm/appointments') });
    }
    if (canAccess('crm.pipeline_config')) {
      crmChildren.push({ key: 'crm-gate-controller', icon: <SettingOutlined />, label: 'Gate Controller', onClick: () => navigate('/crm/gate-controller') });
    }
    if (canAccess('crm.settings')) {
      crmChildren.push({ key: 'crm-form-fields', icon: <DatabaseOutlined />, label: 'Form Fields', onClick: () => navigate('/crm/form-fields') });
      crmChildren.push({ key: 'crm-follow-up-rules', icon: <ThunderboltOutlined />, label: 'Follow-Up Rules', onClick: () => navigate('/crm/admin/follow-up-rules') });
      crmChildren.push({ key: 'crm-follow-up-settings', icon: <SettingOutlined />, label: 'Follow-Up Settings', onClick: () => navigate('/crm/admin/follow-up-settings') });
      crmChildren.push({ key: 'crm-follow-up-observability', icon: <BarChartOutlined />, label: 'Follow-Up Stats', onClick: () => navigate('/crm/admin/follow-up-observability') });
      crmChildren.push({ key: 'crm-client-messages', icon: <SendOutlined />, label: 'Client Messages', onClick: () => navigate('/crm/client-messages') });
    }
    if (crmChildren.length > 0) {
      menuItems.push({ type: 'divider', key: 'crm-divider' });
      menuItems.push({ key: 'crm-group', label: 'CRM', type: 'group', children: crmChildren });
    }
  }

  // ── Calendar Module (company-wide scheduling hub) ──
  const calendarChildren: any[] = [];
  if (canAccess('calendar.events')) {
    calendarChildren.push({
      key: 'calendar-home',
      icon: <CalendarOutlined />,
      label: 'Calendar',
      onClick: () => navigate('/calendar'),
    });
  }
  if (canAccess('calendar.rooms')) {
    calendarChildren.push({
      key: 'calendar-rooms',
      icon: <HomeOutlined />,
      label: 'Meeting Rooms',
      onClick: () => navigate('/calendar/rooms'),
    });
  }
  if (calendarChildren.length > 0) {
    menuItems.push({ type: 'divider', key: 'calendar-divider' });
    menuItems.push({ key: 'calendar-group', label: 'CALENDAR', type: 'group', children: calendarChildren });
  }

  // ── Inventory Module (Phase 1: Code Dictionary; Phase 2: Warehouses / Locations / Stock) ──
  const inventoryChildren: any[] = [];
  // Inventory Phase 15 — Dashboard at top
  if (canAccess('inventory.stock')) {
    inventoryChildren.push({
      key: 'inv-dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/inventory/dashboard'),
    });
  }
  if (canAccess('inventory.warehouse')) {
    inventoryChildren.push({
      key: 'inv-warehouses',
      icon: <ShopOutlined />,
      label: 'Warehouses',
      onClick: () => navigate('/inventory/warehouses'),
    });
  }
  if (canAccess('inventory.stock')) {
    inventoryChildren.push({
      key: 'inv-stock-on-hand',
      icon: <InboxOutlined />,
      label: 'Stock On Hand',
      onClick: () => navigate('/inventory/stock-on-hand'),
    });
  }
  if (canAccess('inventory.move')) {
    inventoryChildren.push({
      key: 'inv-stock-moves',
      icon: <RollbackOutlined />,
      label: 'Stock Moves',
      onClick: () => navigate('/inventory/stock-moves'),
    });
  }
  if (canAccess('inventory.picking')) {
    inventoryChildren.push({
      key: 'inv-pickings',
      icon: <FileTextOutlined />,
      label: 'Pickings',
      onClick: () => navigate('/inventory/pickings'),
    });
  }
  if (canAccess('inventory.lot')) {
    inventoryChildren.push({
      key: 'inv-lots',
      icon: <DatabaseOutlined />,
      label: 'Lots',
      onClick: () => navigate('/inventory/lots'),
    });
  }
  if (canAccess('inventory.quality')) {
    inventoryChildren.push({
      key: 'inv-serials',
      icon: <BarcodeOutlined />,
      label: 'Serial Numbers',
      onClick: () => navigate('/inventory/serial-numbers'),
    });
    inventoryChildren.push({
      key: 'inv-quality',
      icon: <SafetyCertificateOutlined />,
      label: 'Quality',
      onClick: () => navigate('/inventory/quality/inspections'),
    });
  }
  if (canAccess('inventory.warehouse')) {
    inventoryChildren.push({
      key: 'inv-putaway',
      icon: <NodeIndexOutlined />,
      label: 'Putaway Rules',
      onClick: () => navigate('/inventory/putaway-rules'),
    });
  }
  if (canAccess('inventory.adjustment')) {
    inventoryChildren.push({
      key: 'inv-audit-import',
      icon: <FileExcelOutlined />,
      label: 'Audit Import',
      onClick: () => navigate('/inventory/audit-import'),
    });
  }
  if (canAccess('inventory.adjustment')) {
    inventoryChildren.push({
      key: 'inv-landed-costs',
      icon: <WalletOutlined />,
      label: 'Landed Costs',
      onClick: () => navigate('/inventory/landed-costs'),
    });
    inventoryChildren.push({
      key: 'inv-revaluation',
      icon: <CalculatorOutlined />,
      label: 'FX Revaluation',
      onClick: () => navigate('/inventory/revaluation'),
    });
  }
  if (canAccess('inventory.stock')) {
    inventoryChildren.push({
      key: 'inv-valuation-layers',
      icon: <BarChartOutlined />,
      label: 'Valuation Layers',
      onClick: () => navigate('/inventory/valuation-layers'),
    });
  }
  if (canAccess('inventory.code_dictionary')) {
    inventoryChildren.push({
      key: 'inv-code-dictionary',
      icon: <DatabaseOutlined />,
      label: 'Code Dictionary',
      onClick: () => navigate('/inventory/code-dictionary'),
    });
  }
  // Inventory Phase 8 — Physical Counts, ABC, Cycle Counts, Approval Rules
  if (canAccess('inventory.count')) {
    inventoryChildren.push({
      key: 'inv-physical-counts',
      icon: <UnorderedListOutlined />,
      label: 'Physical Counts',
      onClick: () => navigate('/inventory/physical-counts'),
    });
    inventoryChildren.push({
      key: 'inv-cycle-count-policies',
      icon: <ScheduleOutlined />,
      label: 'Cycle Count Policies',
      onClick: () => navigate('/inventory/cycle-count-policies'),
    });
    inventoryChildren.push({
      key: 'inv-cycle-count-schedule',
      icon: <ClockCircleOutlined />,
      label: 'Cycle Count Scheduler',
      onClick: () => navigate('/inventory/cycle-count-schedule'),
    });
  }
  if (canAccess('inventory.stock')) {
    inventoryChildren.push({
      key: 'inv-abc-report',
      icon: <BarChartOutlined />,
      label: 'ABC Classification',
      onClick: () => navigate('/inventory/abc-report'),
    });
  }
  if (canAccess('inventory.approval')) {
    inventoryChildren.push({
      key: 'inv-approval-rules',
      icon: <SafetyOutlined />,
      label: 'Approval Rules',
      onClick: () => navigate('/inventory/approval-rules'),
    });
  }
  // Inventory Phase 9 — Kits, Consignment, Dropship, In-Transit
  if (canAccess('inventory.kit')) {
    inventoryChildren.push({
      key: 'inv-kits',
      icon: <AppstoreOutlined />,
      label: 'Kits',
      onClick: () => navigate('/inventory/kits'),
    });
    inventoryChildren.push({
      key: 'inv-kit-assemble',
      icon: <AppstoreOutlined />,
      label: 'Kit Assembly',
      onClick: () => navigate('/inventory/kit-assemble'),
    });
  }
  if (canAccess('inventory.stock')) {
    inventoryChildren.push({
      key: 'inv-consignment',
      icon: <ShopOutlined />,
      label: 'Consignment Stock',
      onClick: () => navigate('/inventory/consignment'),
    });
    inventoryChildren.push({
      key: 'inv-dropship',
      icon: <NodeIndexOutlined />,
      label: 'Dropship',
      onClick: () => navigate('/inventory/dropship'),
    });
    inventoryChildren.push({
      key: 'inv-in-transit',
      icon: <NodeIndexOutlined />,
      label: 'In-Transit',
      onClick: () => navigate('/inventory/in-transit'),
    });
  }
  // Inventory Phase 10 — RMA
  if (canAccess('inventory.rma')) {
    inventoryChildren.push({
      key: 'inv-rmas',
      icon: <RollbackOutlined />,
      label: 'RMA',
      onClick: () => navigate('/inventory/rmas'),
    });
  }
  // Inventory Phase 11 — Demand Forecasting & MRP
  if (canAccess('inventory.mrp')) {
    inventoryChildren.push({
      key: 'inv-forecasts',
      icon: <LineChartOutlined />,
      label: 'Demand Forecast',
      onClick: () => navigate('/inventory/forecasts'),
    });
    inventoryChildren.push({
      key: 'inv-mrp-runs',
      icon: <ThunderboltOutlined />,
      label: 'MRP Runs',
      onClick: () => navigate('/inventory/mrp/runs'),
    });
    inventoryChildren.push({
      key: 'inv-mrp-proposals',
      icon: <UnorderedListOutlined />,
      label: 'MRP Proposals',
      onClick: () => navigate('/inventory/mrp/proposals'),
    });
    inventoryChildren.push({
      key: 'inv-projected',
      icon: <AreaChartOutlined />,
      label: 'Projected On-Hand',
      onClick: () => navigate('/inventory/projected-on-hand'),
    });
  }
  if (canAccess('inventory.reorder_rule')) {
    inventoryChildren.push({
      key: 'inv-reorder-alerts',
      icon: <AlertOutlined />,
      label: 'Reorder Alerts',
      onClick: () => navigate('/inventory/reorder-alerts'),
    });
    inventoryChildren.push({
      key: 'inv-reorder-rules',
      icon: <SettingOutlined />,
      label: 'Reorder Rules',
      onClick: () => navigate('/inventory/reorder-rules'),
    });
  }
  // Inventory Phase 12 — WIP Accounting
  if (canAccess('inventory.move')) {
    inventoryChildren.push({
      key: 'inv-wip-valuation',
      icon: <FundOutlined />,
      label: 'WIP Valuation',
      onClick: () => navigate('/inventory/wip/valuation'),
    });
    inventoryChildren.push({
      key: 'inv-wip-aging',
      icon: <ClockCircleOutlined />,
      label: 'WIP Aging',
      onClick: () => navigate('/inventory/wip/aging'),
    });
  }
  // Inventory Phase 13 — Barcode & Scanning
  if (canAccess('inventory.move')) {
    inventoryChildren.push({
      key: 'inv-barcode-labels',
      icon: <BarcodeOutlined />,
      label: 'Barcode Labels',
      onClick: () => navigate('/inventory/barcode-labels'),
    });
    inventoryChildren.push({
      key: 'inv-scanner',
      icon: <ScanOutlined />,
      label: 'Scanner',
      onClick: () => navigate('/inventory/scanner'),
    });
  }
  // Inventory Phase 14 — Reports, Snapshots, Reconciliation
  if (canAccess('inventory.move')) {
    inventoryChildren.push({
      key: 'inv-report-aging',
      icon: <PieChartOutlined />,
      label: 'Stock Aging',
      onClick: () => navigate('/inventory/reports/aging'),
    });
    inventoryChildren.push({
      key: 'inv-report-dead-stock',
      icon: <ExclamationCircleOutlined />,
      label: 'Dead Stock',
      onClick: () => navigate('/inventory/reports/dead-stock'),
    });
    inventoryChildren.push({
      key: 'inv-report-slow-moving',
      icon: <ClockCircleOutlined />,
      label: 'Slow Moving',
      onClick: () => navigate('/inventory/reports/slow-moving'),
    });
    inventoryChildren.push({
      key: 'inv-report-turnover',
      icon: <LineChartOutlined />,
      label: 'Stock Turnover',
      onClick: () => navigate('/inventory/reports/turnover'),
    });
    inventoryChildren.push({
      key: 'inv-report-reconciliation',
      icon: <ReconciliationOutlined />,
      label: 'Reconciliation',
      onClick: () => navigate('/inventory/reports/reconciliation'),
    });
    inventoryChildren.push({
      key: 'inv-report-snapshots',
      icon: <CameraOutlined />,
      label: 'Snapshots',
      onClick: () => navigate('/inventory/reports/snapshots'),
    });
    inventoryChildren.push({
      key: 'inv-report-stock-at',
      icon: <HistoryOutlined />,
      label: 'Point-in-Time',
      onClick: () => navigate('/inventory/reports/stock-at'),
    });
  }
  // Inventory Phase 15 — Webhooks (admin only, and only when the inventory module exists on the server)
  if (isAdmin && canAccess('inventory')) {
    inventoryChildren.push({
      key: 'inv-webhooks',
      icon: <ApiOutlined />,
      label: 'Webhooks',
      onClick: () => navigate('/inventory/webhooks'),
    });
    inventoryChildren.push({
      key: 'inv-webhook-deliveries',
      icon: <SendOutlined />,
      label: 'Webhook Log',
      onClick: () => navigate('/inventory/webhooks/deliveries'),
    });
  }
  if (inventoryChildren.length > 0) {
    menuItems.push({ type: 'divider', key: 'inv-divider' });
    menuItems.push({ key: 'inv-group', label: 'INVENTORY', type: 'group', children: inventoryChildren });
  }

  // ── Accounting Module (Phase 21 — permission-gated) ──
  const accChildren: any[] = [];
  if (canAccess('accounting.reports')) {
    accChildren.push({ key: 'acc-dashboard', icon: <FundOutlined />, label: 'Dashboard', onClick: () => navigate('/accounting/dashboard') });
  }
  if (canAccess('accounting.journal_entries')) {
    accChildren.push({ key: 'acc-journal-kanban', icon: <AppstoreOutlined />, label: 'Journal Kanban', onClick: () => navigate('/accounting/journal-kanban') });
  }
  if (canAccess('accounting.invoices')) {
    accChildren.push({ key: 'acc-invoices', icon: <DollarOutlined />, label: 'Invoices', onClick: () => navigate('/accounting/invoices') });
  }
  if (canAccess('accounting.vendor_bills')) {
    accChildren.push({ key: 'acc-vendor-bills', icon: <ShoppingCartOutlined />, label: 'Vendor Bills', onClick: () => navigate('/accounting/vendor-bills') });
  }
  if (canAccess('accounting.journal_entries')) {
    accChildren.push({ key: 'acc-accounts', icon: <AccountBookOutlined />, label: 'Chart of Accounts', onClick: () => navigate('/accounting/accounts') });
  }
  if (canAccess('accounting.cost_centers')) {
    accChildren.push({ key: 'acc-cost-centers', icon: <ApartmentOutlined />, label: 'Cost Centers', onClick: () => navigate('/accounting/cost-centers') });
  }
  if (canAccess('accounting.journal_entries')) {
    accChildren.push({ key: 'acc-entries', icon: <FileTextOutlined />, label: 'Journal Entries', onClick: () => navigate('/accounting/entries') });
  }
  if (canAccess('accounting.payments')) {
    accChildren.push({ key: 'acc-payments', icon: <WalletOutlined />, label: 'Payments', onClick: () => navigate('/accounting/payments') });
  }
  if (canAccess('accounting.reconciliation')) {
    accChildren.push({ key: 'acc-reconciliation', icon: <LinkOutlined />, label: 'Reconciliation', onClick: () => navigate('/accounting/reconciliation') });
  }
  if (canAccess('accounting.bank_reconciliation')) {
    accChildren.push({ key: 'acc-bank-reconciliation', icon: <BankOutlined />, label: 'Bank Reconciliation', onClick: () => navigate('/accounting/bank-reconciliation') });
  }
  if (canAccess('accounting.reports')) {
    accChildren.push({ key: 'acc-trial-balance', icon: <BarChartOutlined />, label: 'Trial Balance', onClick: () => navigate('/accounting/trial-balance') });
    accChildren.push({ key: 'acc-ledger', icon: <BankOutlined />, label: 'General Ledger', onClick: () => navigate('/accounting/ledger') });
    accChildren.push({ key: 'acc-balance-sheet', icon: <BarChartOutlined />, label: 'Balance Sheet', onClick: () => navigate('/accounting/balance-sheet') });
    accChildren.push({ key: 'acc-profit-and-loss', icon: <BarChartOutlined />, label: 'Profit & Loss', onClick: () => navigate('/accounting/profit-and-loss') });
    accChildren.push({ key: 'acc-cash-flow', icon: <BarChartOutlined />, label: 'Cash Flow', onClick: () => navigate('/accounting/cash-flow') });
    accChildren.push({ key: 'acc-aged-receivables', icon: <FileTextOutlined />, label: 'Aged Receivables', onClick: () => navigate('/accounting/aged-receivables') });
    accChildren.push({ key: 'acc-aged-payables', icon: <FileTextOutlined />, label: 'Aged Payables', onClick: () => navigate('/accounting/aged-payables') });
    accChildren.push({ key: 'acc-partner-ledger', icon: <FileTextOutlined />, label: 'Partner Ledger', onClick: () => navigate('/accounting/partner-ledger') });
    accChildren.push({ key: 'acc-vat-return', icon: <FileTextOutlined />, label: 'VAT Return (Form 10)', onClick: () => navigate('/accounting/vat-return') });
    accChildren.push({ key: 'acc-withholding-tax', icon: <PercentageOutlined />, label: 'Withholding Tax', onClick: () => navigate('/accounting/withholding-tax') });
  }
  if (canAccess('accounting.journal_entries')) {
    accChildren.push({ key: 'acc-currency-rates', icon: <DollarOutlined />, label: 'Currency Rates', onClick: () => navigate('/accounting/currency-rates') });
    accChildren.push({ key: 'acc-fx-revaluation', icon: <CalculatorOutlined />, label: 'FX Revaluation', onClick: () => navigate('/accounting/fx-revaluation') });
  }
  if (canAccess('accounting.fiscal_year')) {
    accChildren.push({ key: 'acc-fiscal-periods', icon: <CalendarOutlined />, label: 'Fiscal Year & Locks', onClick: () => navigate('/accounting/fiscal-periods') });
  }
  if (canAccess('accounting.collections')) {
    accChildren.push({ key: 'acc-collections', icon: <ExclamationCircleOutlined />, label: 'AR Collections', onClick: () => navigate('/accounting/collections') });
    accChildren.push({ key: 'acc-collection-settings', icon: <SettingOutlined />, label: 'Collection Levels', onClick: () => navigate('/accounting/collections/settings') });
  }
  if (canAccess('accounting.fixed_assets')) {
    accChildren.push({ key: 'acc-assets', icon: <CalculatorOutlined />, label: 'Fixed Assets', onClick: () => navigate('/accounting/assets') });
  }
  if (canAccess('accounting.journal_entries')) {
    accChildren.push({ key: 'acc-deferred', icon: <CalculatorOutlined />, label: 'Deferred Rev/Exp', onClick: () => navigate('/accounting/deferred') });
  }
  if (canAccess('accounting.settings')) {
    accChildren.push({ key: 'acc-audit-compliance', icon: <SafetyCertificateOutlined />, label: 'Audit & Compliance', onClick: () => navigate('/accounting/audit-compliance') });
  }
  if (canAccess('accounting.cost_centers')) {
    accChildren.push({ key: 'acc-cc-analytics', icon: <LineChartOutlined />, label: 'CC Analytics', onClick: () => navigate('/accounting/cost-center-analytics') });
    accChildren.push({ key: 'acc-deal-profitability', icon: <FundProjectionScreenOutlined />, label: 'Deal Profitability', onClick: () => navigate('/accounting/deal-profitability') });
  }
  if (canAccess('accounting.settings')) {
    accChildren.push({ key: 'acc-bridge-health', icon: <NodeIndexOutlined />, label: 'Bridge Health', onClick: () => navigate('/accounting/bridge-health') });
    accChildren.push({ key: 'acc-settings', icon: <SettingOutlined />, label: 'Settings', onClick: () => navigate('/accounting/settings') });
  }
  if (accChildren.length > 0) {
    menuItems.push({ type: 'divider', key: 'acc-divider' });
    menuItems.push({ key: 'acc-group', label: 'ACCOUNTING', type: 'group', children: accChildren });
  }

  // ── System Module ──
  if (isAdmin || canAccess('admin') || canAccess('admin.vendors') || canAccess('admin.categories') || canAccess('admin.units') || canAccess('admin.users') || canAccess('admin.data') || canAccess('admin.permissions')) {
    menuItems.push({ type: 'divider', key: 'admin-divider' });
    menuItems.push({
      key: 'admin-group',
      label: 'SYSTEM',
      type: 'group',
      children: [
        { key: 'admin', icon: <SettingOutlined />, label: 'Admin', onClick: () => navigate('/admin') },
      ],
    });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const siderContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <img
          src="/logo.PNG"
          alt="Shahira Lasheen"
          style={{ width: collapsed && !isMobile ? 40 : 130, height: 'auto', filter: 'invert(1)', transition: 'width 0.2s' }}
        />
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          style={{ background: '#000', borderRight: 'none' }}
        />
      </div>
      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-end',
            padding: collapsed ? 0 : '0 16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.45)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'transparent'; }}
        >
          {collapsed ? <RightOutlined style={{ fontSize: 14 }} /> : (
            <Space size={8}>
              <Text style={{ color: 'inherit', fontSize: 12 }}>Collapse</Text>
              <LeftOutlined style={{ fontSize: 12 }} />
            </Space>
          )}
        </div>
      )}
    </div>
  );

  const currentMarginLeft = isMobile ? 0 : (collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop: fixed sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          width={SIDER_WIDTH}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#000',
            zIndex: 100,
          }}
          trigger={null}
        >
          {siderContent}
        </Sider>
      )}

      {/* Mobile: drawer sidebar */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={250}
          bodyStyle={{ padding: 0, background: '#000' }}
          headerStyle={{ display: 'none' }}
        >
          {siderContent}
        </Drawer>
      )}

      <Layout style={{ marginLeft: currentMarginLeft, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ fontSize: 18 }}
            />
          ) : (
            <div />
          )}
          <Space size={isMobile ? 'small' : 'middle'}>
            {canAccess('purchasing') && <GlobalSearch compact={isMobile} />}
            <NotificationBell />
            {location.pathname.startsWith('/crm') && canAccess('crm.clients', 'create') && (
              <Button
                type="primary"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => setCaptureOpen(true)}
              >
                {!isMobile && 'New Lead'}
              </Button>
            )}
            {!isMobile && (
              <Space>
                <UserOutlined />
                <Text strong>{user?.displayName}</Text>
                <Text type="secondary">({user?.departmentCode} - {(user?.roles?.length ? user.roles : [user?.role]).map(r => r?.replace(/_/g, ' ')).join(', ')})</Text>
              </Space>
            )}
            {isMobile && (
              <Text strong style={{ fontSize: 12 }}>{user?.displayName}</Text>
            )}
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              {!isMobile && 'Logout'}
            </Button>
          </Space>
        </Header>
        {/* D18: Global New Lead modal — accessible from any page */}
        {canAccess('crm.clients', 'create') && (
          <QuickCaptureModal
            open={captureOpen}
            onClose={() => setCaptureOpen(false)}
            onSuccess={(clientId) => {
              setCaptureOpen(false);
              navigate(`/crm/clients/${clientId}`);
            }}
          />
        )}
        <Content style={{
          margin: isMobile ? 8 : 24,
          padding: isMobile ? 12 : 24,
          background: '#fff',
          borderRadius: 8,
          minHeight: 'calc(100vh - 112px)',
          overflow: 'auto',
        }}>
          <Routes>
            <Route path="/" element={<ResourceGuard resource="purchasing.dashboard"><Dashboard /></ResourceGuard>} />
            <Route path="/dashboard" element={<ResourceGuard resource="purchasing.dashboard"><Dashboard /></ResourceGuard>} />
            <Route path="/mrs" element={<ResourceGuard resource="purchasing.mrs"><MRList /></ResourceGuard>} />
            <Route path="/mrs/new" element={<ResourceGuard resource="purchasing.mrs" minLevel="create"><MRCreate /></ResourceGuard>} />
            <Route path="/mrs/:mrId" element={<ResourceGuard resource="purchasing.mrs"><MRView /></ResourceGuard>} />
            <Route path="/mrs/:mrId/edit" element={<ResourceGuard resource="purchasing.mrs" minLevel="create"><MRCreate /></ResourceGuard>} />
            <Route path="/pos" element={<ResourceGuard resource="purchasing.pos"><POList /></ResourceGuard>} />
            <Route path="/pos/new" element={<ResourceGuard resource="purchasing.pos" minLevel="create"><POCreate /></ResourceGuard>} />
            <Route path="/pos/:poId" element={<ResourceGuard resource="purchasing.pos"><POView /></ResourceGuard>} />
            <Route path="/grns" element={<ResourceGuard resource="purchasing.grns"><GRNList /></ResourceGuard>} />
            <Route path="/grns/:grnId" element={<ResourceGuard resource="purchasing.grns"><GRNView /></ResourceGuard>} />
            <Route path="/returns" element={<ResourceGuard resource="purchasing.returns"><Suspense fallback={<Spin size="large" />}><ReturnList /></Suspense></ResourceGuard>} />
            <Route path="/cancellations" element={<ResourceGuard resource="purchasing.pos"><Suspense fallback={<Spin size="large" />}><Cancellations /></Suspense></ResourceGuard>} />
            <Route path="/vendors" element={<ResourceGuard resource="purchasing.vendors"><Suspense fallback={<Spin size="large" />}><VendorList /></Suspense></ResourceGuard>} />
            <Route path="/vendors/pending" element={<ResourceGuard resource="purchasing.vendors" minLevel="full"><Suspense fallback={<Spin size="large" />}><VendorPendingQueue /></Suspense></ResourceGuard>} />
            <Route path="/vendors/:id" element={<ResourceGuard resource="purchasing.vendors"><Suspense fallback={<Spin size="large" />}><VendorDetail /></Suspense></ResourceGuard>} />
            <Route path="/catalog" element={<ResourceGuard resource="purchasing.catalog"><CatalogList /></ResourceGuard>} />
            <Route path="/catalog/composer-sync" element={<ResourceGuard resource="purchasing.catalog" minLevel="full"><Suspense fallback={<Spin size="large" />}><ComposerSync /></Suspense></ResourceGuard>} />
            <Route path="/catalog/composer-sync/:batchId" element={<ResourceGuard resource="purchasing.catalog" minLevel="full"><Suspense fallback={<Spin size="large" />}><ComposerSync /></Suspense></ResourceGuard>} />
            <Route path="/catalog/:id" element={<ResourceGuard resource="purchasing.catalog"><Suspense fallback={<Spin size="large" />}><ItemCard /></Suspense></ResourceGuard>} />
            <Route path="/client-samples" element={<ResourceGuard resource="purchasing.client_samples"><Suspense fallback={<Spin size="large" />}><ClientSamples /></Suspense></ResourceGuard>} />
            <Route path="/cost-allocation" element={<ResourceGuard resource="purchasing.cost_centers"><Suspense fallback={<Spin size="large" />}><CostAllocation /></Suspense></ResourceGuard>} />
            <Route path="/audit" element={<ResourceGuard resource="purchasing.audit"><AuditTrail /></ResourceGuard>} />
            <Route path="/admin" element={<Admin />} />
            {/* Inventory Routes — Phase 1 */}
            <Route
              path="/inventory/code-dictionary"
              element={
                <ResourceGuard resource="inventory.code_dictionary">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryCodeDictionary />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Routes — Phase 2 */}
            <Route
              path="/inventory/warehouses"
              element={
                <ResourceGuard resource="inventory.warehouse">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryWarehouseList />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/warehouses/new"
              element={
                <ResourceGuard resource="inventory.warehouse" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryWarehouseForm />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/warehouses/:warehouseId/edit"
              element={
                <ResourceGuard resource="inventory.warehouse" minLevel="full">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryWarehouseForm />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/warehouses/:warehouseId/locations"
              element={
                <ResourceGuard resource="inventory.location">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLocationTree />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-on-hand"
              element={
                <ResourceGuard resource="inventory.stock">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryStockOnHand />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Routes — Phase 3 */}
            <Route
              path="/inventory/stock-moves"
              element={
                <ResourceGuard resource="inventory.move">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryStockMoveHistory />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-moves/new-receipt"
              element={
                <ResourceGuard resource="inventory.receipt" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryReceiptCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-moves/new-issue"
              element={
                <ResourceGuard resource="inventory.move" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryIssueCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-moves/new-transfer"
              element={
                <ResourceGuard resource="inventory.transfer" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryTransferCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-moves/new-scrap"
              element={
                <ResourceGuard resource="inventory.scrap" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryScrapCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/stock-moves/:id"
              element={
                <ResourceGuard resource="inventory.move">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryStockMoveView />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/pickings"
              element={
                <ResourceGuard resource="inventory.picking">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryPickingList />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/pickings/new"
              element={
                <ResourceGuard resource="inventory.picking" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryPickingCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/pickings/:id"
              element={
                <ResourceGuard resource="inventory.picking">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryPickingView />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/lots"
              element={
                <ResourceGuard resource="inventory.lot">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLotList />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/lots/:id"
              element={
                <ResourceGuard resource="inventory.lot">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLotView />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 4 — Serial numbers */}
            <Route
              path="/inventory/serial-numbers"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventorySerialNumberList />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/serial-numbers/new"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventorySerialNumberForm />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/serial-numbers/:id"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventorySerialNumberView />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/serial-numbers/:id/edit"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventorySerialNumberForm />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 4 — Quality inspection */}
            <Route
              path="/inventory/quality/plans"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryQualityPlans />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/quality/inspections"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryQualityQueue />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/quality/inspections/:id/record"
              element={
                <ResourceGuard resource="inventory.quality">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryQualityForm />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 4 — Putaway rules */}
            <Route
              path="/inventory/putaway-rules"
              element={
                <ResourceGuard resource="inventory.warehouse">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryPutawayRules />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 5 — Audit opening-balance import */}
            <Route
              path="/inventory/audit-import"
              element={
                <ResourceGuard resource="inventory.adjustment">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryAuditImport />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 7 — Landed Cost + FX Revaluation + Valuation Layers */}
            <Route
              path="/inventory/landed-costs"
              element={
                <ResourceGuard resource="inventory.adjustment">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLandedCostList />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/landed-costs/new"
              element={
                <ResourceGuard resource="inventory.adjustment" minLevel="create">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLandedCostCreate />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/landed-costs/:id"
              element={
                <ResourceGuard resource="inventory.adjustment">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryLandedCostView />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/revaluation"
              element={
                <ResourceGuard resource="inventory.adjustment">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryRevaluationReport />
                  </Suspense>
                </ResourceGuard>
              }
            />
            <Route
              path="/inventory/valuation-layers"
              element={
                <ResourceGuard resource="inventory.stock">
                  <Suspense fallback={<Spin size="large" />}>
                    <InventoryValuationLayers />
                  </Suspense>
                </ResourceGuard>
              }
            />
            {/* Inventory Phase 8 — Physical Counts, ABC, Cycle Counts, Approval Rules */}
            <Route
              path="/inventory/physical-counts"
              element={<ResourceGuard resource="inventory.count"><Suspense fallback={<Spin size="large" />}><PhysicalCountList /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/physical-counts/new"
              element={<ResourceGuard resource="inventory.count" minLevel="create"><Suspense fallback={<Spin size="large" />}><PhysicalCountCreate /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/physical-counts/:id"
              element={<ResourceGuard resource="inventory.count"><Suspense fallback={<Spin size="large" />}><PhysicalCountView /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/cycle-count-policies"
              element={<ResourceGuard resource="inventory.count"><Suspense fallback={<Spin size="large" />}><CycleCountPolicies /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/cycle-count-schedule"
              element={<ResourceGuard resource="inventory.count"><Suspense fallback={<Spin size="large" />}><CycleCountSchedule /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/abc-report"
              element={<ResourceGuard resource="inventory.stock"><Suspense fallback={<Spin size="large" />}><AbcClassificationReport /></Suspense></ResourceGuard>}
            />
            <Route
              path="/inventory/approval-rules"
              element={<ResourceGuard resource="inventory.approval"><Suspense fallback={<Spin size="large" />}><ApprovalRules /></Suspense></ResourceGuard>}
            />
            {/* Inventory Phase 9 — Kits, Consignment, Dropship, In-Transit */}
            <Route path="/inventory/kits" element={<ResourceGuard resource="inventory.kit"><Suspense fallback={<Spin size="large" />}><KitList /></Suspense></ResourceGuard>} />
            <Route path="/inventory/kits/new" element={<ResourceGuard resource="inventory.kit" minLevel="create"><Suspense fallback={<Spin size="large" />}><KitBuilder /></Suspense></ResourceGuard>} />
            <Route path="/inventory/kits/:id" element={<ResourceGuard resource="inventory.kit"><Suspense fallback={<Spin size="large" />}><KitBuilder /></Suspense></ResourceGuard>} />
            <Route path="/inventory/kit-assemble" element={<ResourceGuard resource="inventory.kit" minLevel="create"><Suspense fallback={<Spin size="large" />}><KitAssemble /></Suspense></ResourceGuard>} />
            <Route path="/inventory/consignment" element={<ResourceGuard resource="inventory.stock"><Suspense fallback={<Spin size="large" />}><ConsignmentReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/dropship" element={<ResourceGuard resource="inventory.stock"><Suspense fallback={<Spin size="large" />}><DropshipPickings /></Suspense></ResourceGuard>} />
            <Route path="/inventory/in-transit" element={<ResourceGuard resource="inventory.stock"><Suspense fallback={<Spin size="large" />}><InTransitTransfers /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 10 — RMA */}
            <Route path="/inventory/rmas" element={<ResourceGuard resource="inventory.rma"><Suspense fallback={<Spin size="large" />}><RmaList /></Suspense></ResourceGuard>} />
            <Route path="/inventory/rmas/new" element={<ResourceGuard resource="inventory.rma" minLevel="create"><Suspense fallback={<Spin size="large" />}><RmaCreate /></Suspense></ResourceGuard>} />
            <Route path="/inventory/rmas/:id" element={<ResourceGuard resource="inventory.rma"><Suspense fallback={<Spin size="large" />}><RmaView /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 11 — Demand Forecasting & MRP */}
            <Route path="/inventory/forecasts" element={<ResourceGuard resource="inventory.mrp"><Suspense fallback={<Spin size="large" />}><ForecastViewer /></Suspense></ResourceGuard>} />
            <Route path="/inventory/mrp/runs" element={<ResourceGuard resource="inventory.mrp"><Suspense fallback={<Spin size="large" />}><MrpRunHistory /></Suspense></ResourceGuard>} />
            <Route path="/inventory/mrp/proposals" element={<ResourceGuard resource="inventory.mrp"><Suspense fallback={<Spin size="large" />}><MrpRunProposals /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reorder-alerts" element={<ResourceGuard resource="inventory.reorder_rule"><Suspense fallback={<Spin size="large" />}><ReorderAlerts /></Suspense></ResourceGuard>} />
            <Route path="/inventory/projected-on-hand" element={<ResourceGuard resource="inventory.mrp"><Suspense fallback={<Spin size="large" />}><ProjectedOnHandChart /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reorder-rules" element={<ResourceGuard resource="inventory.reorder_rule"><Suspense fallback={<Spin size="large" />}><ReorderRulesAdmin /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 12 — WIP Accounting */}
            <Route path="/inventory/wip/valuation" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><WipValuationReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/wip/aging" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><WipAgingReport /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 13 — Barcode & Scanning */}
            <Route path="/inventory/barcode-labels" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><BarcodeLabels /></Suspense></ResourceGuard>} />
            <Route path="/inventory/scanner" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><MobileScanner /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 14 — Reports, Snapshots, Reconciliation */}
            <Route path="/inventory/reports/aging" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><StockAgingReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/dead-stock" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><DeadStockReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/slow-moving" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><SlowMovingReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/turnover" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><StockTurnoverReport /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/reconciliation" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><ReconciliationDashboard /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/snapshots" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><SnapshotAdmin /></Suspense></ResourceGuard>} />
            <Route path="/inventory/reports/stock-at" element={<ResourceGuard resource="inventory.move"><Suspense fallback={<Spin size="large" />}><PointInTimeQuery /></Suspense></ResourceGuard>} />
            {/* Inventory Phase 15 — Dashboard, Webhooks */}
            <Route path="/inventory/dashboard" element={<ResourceGuard resource="inventory.stock"><Suspense fallback={<Spin size="large" />}><InventoryDashboard /></Suspense></ResourceGuard>} />
            <Route path="/inventory/webhooks" element={<Suspense fallback={<Spin size="large" />}><WebhookSubscriptions /></Suspense>} />
            <Route path="/inventory/webhooks/deliveries" element={<Suspense fallback={<Spin size="large" />}><WebhookDeliveries /></Suspense>} />
            {/* Accounting Routes */}
            <Route path="/accounting/*" element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><Spin size="large" tip="Loading..." /></div>}>
                <Routes>
                  {/* Invoices — accounting.invoices */}
                  <Route path="/invoices" element={<ResourceGuard resource="accounting.invoices"><AccInvoiceList /></ResourceGuard>} />
                  <Route path="/invoices/new" element={<ResourceGuard resource="accounting.invoices" minLevel="create"><AccInvoiceCreate /></ResourceGuard>} />
                  <Route path="/invoices/:id" element={<ResourceGuard resource="accounting.invoices"><AccInvoiceView /></ResourceGuard>} />
                  {/* Vendor Bills — accounting.vendor_bills */}
                  <Route path="/vendor-bills" element={<ResourceGuard resource="accounting.vendor_bills"><AccVendorBillList /></ResourceGuard>} />
                  <Route path="/vendor-bills/new" element={<ResourceGuard resource="accounting.vendor_bills" minLevel="create"><AccVendorBillCreate /></ResourceGuard>} />
                  <Route path="/vendor-bills/:id" element={<ResourceGuard resource="accounting.vendor_bills"><AccVendorBillView /></ResourceGuard>} />
                  {/* Chart of Accounts & Cost Centers */}
                  <Route path="/accounts" element={<ResourceGuard resource="accounting.journal_entries"><AccChartOfAccounts /></ResourceGuard>} />
                  <Route path="/cost-centers" element={<ResourceGuard resource="accounting.cost_centers"><AccCostCenters /></ResourceGuard>} />
                  {/* Journal Entries — accounting.journal_entries */}
                  <Route path="/entries" element={<ResourceGuard resource="accounting.journal_entries"><AccJournalEntries /></ResourceGuard>} />
                  <Route path="/entries/new" element={<ResourceGuard resource="accounting.journal_entries" minLevel="create"><AccJournalEntryCreate /></ResourceGuard>} />
                  <Route path="/entries/:id" element={<ResourceGuard resource="accounting.journal_entries"><AccJournalEntryView /></ResourceGuard>} />
                  {/* Payments — accounting.payments */}
                  <Route path="/payments" element={<ResourceGuard resource="accounting.payments"><AccPayments /></ResourceGuard>} />
                  <Route path="/payments/:id" element={<ResourceGuard resource="accounting.payments"><AccPaymentView /></ResourceGuard>} />
                  {/* Reconciliation — accounting.reconciliation */}
                  <Route path="/reconciliation" element={<ResourceGuard resource="accounting.reconciliation"><AccReconciliationView /></ResourceGuard>} />
                  {/* Financial Reports — accounting.reports */}
                  <Route path="/trial-balance" element={<ResourceGuard resource="accounting.reports"><AccTrialBalance /></ResourceGuard>} />
                  <Route path="/ledger" element={<ResourceGuard resource="accounting.reports"><AccGeneralLedger /></ResourceGuard>} />
                  <Route path="/balance-sheet" element={<ResourceGuard resource="accounting.reports"><AccBalanceSheet /></ResourceGuard>} />
                  <Route path="/profit-and-loss" element={<ResourceGuard resource="accounting.reports"><AccProfitAndLoss /></ResourceGuard>} />
                  <Route path="/cash-flow" element={<ResourceGuard resource="accounting.reports"><AccCashFlow /></ResourceGuard>} />
                  <Route path="/aged-receivables" element={<ResourceGuard resource="accounting.reports"><AccAgedReceivables /></ResourceGuard>} />
                  <Route path="/aged-payables" element={<ResourceGuard resource="accounting.reports"><AccAgedPayables /></ResourceGuard>} />
                  <Route path="/partner-ledger" element={<ResourceGuard resource="accounting.reports"><AccPartnerLedger /></ResourceGuard>} />
                  <Route path="/dashboard" element={<ResourceGuard resource="accounting.reports"><AccDashboard /></ResourceGuard>} />
                  <Route path="/journal-kanban" element={<ResourceGuard resource="accounting.journal_entries"><AccJournalKanban /></ResourceGuard>} />
                  {/* Bank Reconciliation — accounting.bank_reconciliation */}
                  <Route path="/bank-reconciliation" element={<ResourceGuard resource="accounting.bank_reconciliation"><AccBankReconciliation /></ResourceGuard>} />
                  <Route path="/bank-reconciliation/:id" element={<ResourceGuard resource="accounting.bank_reconciliation"><AccBankReconciliationWorkspace /></ResourceGuard>} />
                  {/* Accounting Phase 10 — Tax Reports */}
                  <Route path="/vat-return" element={<ResourceGuard resource="accounting.reports"><AccVATReturn /></ResourceGuard>} />
                  <Route path="/withholding-tax" element={<ResourceGuard resource="accounting.reports"><AccWithholdingTaxReport /></ResourceGuard>} />
                  {/* Accounting Phase 11 — Multi-Currency */}
                  <Route path="/currency-rates" element={<ResourceGuard resource="accounting.journal_entries"><AccCurrencyRates /></ResourceGuard>} />
                  <Route path="/fx-revaluation" element={<ResourceGuard resource="accounting.journal_entries" minLevel="full"><AccFxRevaluation /></ResourceGuard>} />
                  {/* Accounting Phase 12 — Fiscal Year & Lock Dates */}
                  <Route path="/fiscal-periods" element={<ResourceGuard resource="accounting.fiscal_year"><AccFiscalYearSettings /></ResourceGuard>} />
                  {/* Accounting Phase 13 — AR Collections */}
                  <Route path="/collections" element={<ResourceGuard resource="accounting.collections"><AccCollectionManagement /></ResourceGuard>} />
                  <Route path="/collections/settings" element={<ResourceGuard resource="accounting.collections" minLevel="full"><AccCollectionSettings /></ResourceGuard>} />
                  {/* Accounting Phase 15 — Fixed Assets */}
                  <Route path="/assets" element={<ResourceGuard resource="accounting.fixed_assets"><AccAssetList /></ResourceGuard>} />
                  <Route path="/assets/new" element={<ResourceGuard resource="accounting.fixed_assets" minLevel="create"><AccAssetCreate /></ResourceGuard>} />
                  <Route path="/assets/:id" element={<ResourceGuard resource="accounting.fixed_assets"><AccAssetView /></ResourceGuard>} />
                  {/* Accounting Phase 16 — Deferred Revenue & Expenses */}
                  <Route path="/deferred" element={<ResourceGuard resource="accounting.journal_entries"><AccDeferredEntries /></ResourceGuard>} />
                  {/* Accounting Phase 18 — Audit Trail & Compliance */}
                  <Route path="/audit-compliance" element={<ResourceGuard resource="accounting.settings"><AccAuditCompliance /></ResourceGuard>} />
                  {/* Accounting Phase 19 — Bridge Health */}
                  <Route path="/bridge-health" element={<ResourceGuard resource="accounting.settings"><AccBridgeHealth /></ResourceGuard>} />
                  {/* Accounting Phase 20 — Cost Center Deep Analytics */}
                  <Route path="/cost-center-analytics" element={<ResourceGuard resource="accounting.cost_centers"><AccCostCenterAnalytics /></ResourceGuard>} />
                  <Route path="/deal-profitability" element={<ResourceGuard resource="accounting.cost_centers"><AccDealProfitability /></ResourceGuard>} />
                  {/* Accounting Phase 17 — Settings & Configuration */}
                  <Route path="/settings" element={<ResourceGuard resource="accounting.settings" minLevel="full"><AccSettingsPage /></ResourceGuard>} />
                </Routes>
              </Suspense>
            } />
            {/* CRM Routes — wrapped in Suspense for lazy loading (Phase 5.4) */}
            <Route path="/crm/*" element={
              <ResourceGuard resource="crm">
                <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><Spin size="large" tip="Loading..." /></div>}>
                  <Routes>
                    <Route path="/" element={<ResourceGuard resource="crm.dashboard"><CRMDashboard /></ResourceGuard>} />
                    <Route path="/dashboard" element={<ResourceGuard resource="crm.dashboard"><CRMDashboard /></ResourceGuard>} />
                    <Route path="/workspace" element={<ResourceGuard resource="crm.workspace"><CRMWorkspace /></ResourceGuard>} />
                    <Route path="/clients" element={<ResourceGuard resource="crm.clients"><CRMClientList /></ResourceGuard>} />
                    <Route path="/clients/:id" element={<ResourceGuard resource="crm.clients"><CRMClientView /></ResourceGuard>} />
                    <Route path="/clients/:id/edit" element={<ResourceGuard resource="crm.clients" minLevel="create"><CRMClientEdit /></ResourceGuard>} />
                    <Route path="/deals" element={<ResourceGuard resource="crm.deals"><CRMDealList /></ResourceGuard>} />
                    <Route path="/deals/new" element={<ResourceGuard resource="crm.deals" minLevel="create"><CRMDealCreate /></ResourceGuard>} />
                    <Route path="/leads/new" element={<ResourceGuard resource="crm.clients" minLevel="create"><CRMLeadCapture /></ResourceGuard>} />
                    <Route path="/deals/pipeline" element={<ResourceGuard resource="crm.pipeline"><CRMPipeline /></ResourceGuard>} />
                    <Route path="/deals/:id" element={<ResourceGuard resource="crm.deals"><CRMDealView /></ResourceGuard>} />
                    <Route path="/tasks" element={<ResourceGuard resource="crm.tasks"><CRMTaskList /></ResourceGuard>} />
                    <Route path="/follow-ups" element={<ResourceGuard resource="crm.follow_ups"><CRMFollowUps /></ResourceGuard>} />
                    <Route path="/appointments" element={<ResourceGuard resource="crm.appointments"><CRMAppointments /></ResourceGuard>} />
                    <Route path="/gate-controller" element={<ResourceGuard resource="crm.pipeline_config"><CRMGateController /></ResourceGuard>} />
                    <Route path="/form-fields" element={<ResourceGuard resource="crm.settings" minLevel="full"><CRMFormFieldAdmin /></ResourceGuard>} />
                    <Route path="/admin/follow-up-rules" element={<ResourceGuard resource="crm.settings" minLevel="full"><CRMFollowUpRules /></ResourceGuard>} />
                    <Route path="/admin/follow-up-settings" element={<ResourceGuard resource="crm.settings" minLevel="full"><CRMFollowUpSettings /></ResourceGuard>} />
                    <Route path="/admin/follow-up-observability" element={<ResourceGuard resource="crm.settings" minLevel="full"><CRMFollowUpObservability /></ResourceGuard>} />
                    <Route path="/client-messages" element={<ResourceGuard resource="crm.tasks"><CRMClientMessages /></ResourceGuard>} />
                  </Routes>
                </Suspense>
              </ResourceGuard>
            } />
            {/* Calendar Routes — company-wide scheduling hub */}
            <Route path="/calendar" element={
              <ResourceGuard resource="calendar.events">
                <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><Spin size="large" tip="Loading..." /></div>}>
                  <CalendarHome />
                </Suspense>
              </ResourceGuard>
            } />
            <Route path="/calendar/rooms" element={
              <ResourceGuard resource="calendar.rooms">
                <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><Spin size="large" tip="Loading..." /></div>}>
                  <CalendarRoomList />
                </Suspense>
              </ResourceGuard>
            } />
            <Route path="*" element={<Navigate to="/" replace /> } />
          </Routes>
        </Content>
      </Layout>
      <ChatDrawer />
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public style-guide preview — no auth required */}
          <Route path="/design-system" element={
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" tip="Loading..." /></div>}>
              <DesignSystemPrototype />
            </Suspense>
          } />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <PipelineStagesProvider>
                  <NotificationProvider>
                    <AppLayout />
                  </NotificationProvider>
                </PipelineStagesProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
